/**
 * Shared colour palette — the single source of truth for raw colour values
 * that are hard-coded across the app.
 *
 * Colours are named to match the SEEK Braid design-system palette. Only the
 * shades actually referenced in this repo are included.
 *
 * Source: https://github.com/seek-oss/braid-design-system/blob/braid-design-system%4033.6.0/packages/braid-design-system/src/lib/color/palette.ts
 */
export const palette = {
  white: '#FFF',
  black: '#000',

  grey: {
    50: '#F7F8FB',
    75: '#F3F5F7',
    100: '#EAECF1',
    200: '#D2D7DF',
    300: '#ABB3C1',
    400: '#838FA5',
    500: '#5A6881',
    600: '#414C63',
    700: '#2E3849',
    800: '#1C2330',
    900: '#0E131B',
  },

  seekBlue: {
    500: '#0D3880',
  },

  seekBlueLight: {
    50: '#F0F7FE',
    300: '#99BFF7',
    500: '#4B84E7',
  },

  seekPink: {
    100: '#FCD9EF',
    200: '#F8B1DC',
    300: '#F470BD',
    500: '#E60278',
    700: '#A00043',
  },

  red: {
    100: '#FFE3E2',
    300: '#FB999A',
    500: '#F94344',
    700: '#B91E1E',
  },

  orange: {
    300: '#FEBB90',
    500: '#FD7532',
  },

  yellow: {
    100: '#FEF8DE',
    300: '#FEE384',
    400: '#FDD651',
    500: '#FDC221',
    700: '#B9800D',
    800: '#945B07',
  },

  green: {
    300: '#98DE97',
    500: '#3EB73D',
  },

  mint: {
    100: '#E2F7F1',
    200: '#C1EDE1',
    300: '#8BDEC5',
    500: '#28B888',
    700: '#12784F',
  },

  cyan: {
    700: '#137D8B',
  },

  blue: {
    100: '#E3F2FB',
    300: '#98C9F1',
    700: '#1D559D',
  },

  indigo: {
    200: '#CBD5F9',
    300: '#9FB0F5',
  },

  violet: {
    300: '#B5A7F4',
    500: '#6E56E6',
  },

  purple: {
    200: '#F0D6FA',
    300: '#E1B2F5',
    500: '#BE68E8',
  },
} as const;

/**
 * Semi-transparent tokens that aren't part of the Braid palette. These are
 * intentionally shared across light and dark colour modes.
 */
export const transparent = {
  scrim: 'rgba(0, 0, 0, 0.3)',
  // https://github.com/seek-oss/scoobie/blob/248312d5c4677d60d82e2276fb3b5435fcf33b56/src/private/color.ts#L2-L3
  placeholderBorder: 'rgba(51, 51, 51, 0.3)',
  placeholderFill: 'rgba(51, 51, 51, 0.08)',
} as const;

export const dataVisualisation = {
  darkMode: [
    palette.grey[200],
    palette.violet[300],
    palette.purple[300],
    palette.purple[200],
    palette.seekPink[200],
    palette.seekPink[100],
    palette.indigo[200],
    palette.seekBlueLight[300],
    palette.mint[100],
    palette.mint[200],
    palette.mint[300],
    palette.green[300],
    palette.yellow[300],
    palette.yellow[400],
    palette.orange[300],
    palette.red[300],
  ],
  lightMode: [
    palette.cyan[700],
    palette.mint[500],
    palette.green[500],
    palette.seekBlueLight[500],
    palette.grey[500],
    palette.violet[500],
    palette.yellow[800],
    palette.indigo[300],
    palette.seekPink[700],
    palette.purple[500],
    palette.seekPink[500],
    palette.seekPink[300],
    palette.red[500],
    palette.orange[500],
    palette.yellow[500],
  ],
} as const;
